/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import type { DxAPIResponseObject } from '../case/types';

class ObjectTransformer {
  needsMapping: boolean;

  constructor() {
    this.needsMapping = false;
  }

  // Function to return current value of needsMapping flag
  getNeedsMapping() {
    return this.needsMapping;
  }

  // Function to set needsMapping flag
  setNeedsMapping(needsMapping: boolean) {
    this.needsMapping = needsMapping;
  }

  // Function to map case to object in constant string
  map(inputString: string): string {
    if (!this.needsMapping) {
      return inputString;
    }
    return this.replacer(inputString);
  }

  // Function to map object to case
  transformObjectToCase(response: object) {
    let parsedResponse;
    let isParsingFailed = false;
    try {
      const stringifiedResponse = this.replaceToCase(JSON.stringify(response));
      parsedResponse = JSON.parse(stringifiedResponse);
    } catch (e) /* istanbul ignore next */ {
      console.error('not a Valid JSON', e);
      isParsingFailed = true;
    }

    /* istanbul ignore next */
    return isParsingFailed ? response : parsedResponse;
  }

  // Function to transform case to object
  transformCaseData(response: DxAPIResponseObject) {
    const caseInfo = response?.data?.caseInfo;
    const uiResources = response?.uiResources;

    return {
      data: {
        objectInfo: {
          content: { ...caseInfo?.content }
        }
      },
      uiResources: { ...uiResources }
    };
  }

  /* Analyse if response data consists of caseInfo or objectInfo */
  analyseResponse(response: any) {
    if (response?.data?.objectInfo && window?.PCore?.getEnvironmentInfo()?.isUnifiedObject()) {
      this.setNeedsMapping(true);
    } else {
      this.setNeedsMapping(false);
    }
  }

  // This function perform direct replacements
  replacer(inputString: string): string {
    if (this.needsMapping) {
      inputString = inputString
        .replace('caseInfo', 'objectInfo')
        .replace('caseTypeName', 'objectTypeName')
        .replace('caseTypeID', 'objectTypeID');
    }

    return inputString;
  }

  // This function changes object to case fields
  replaceToCase(inputString: string): string {
    return inputString
      .replace('objectInfo', 'caseInfo')
      .replace('objectTypeName', 'caseTypeName')
      .replace('objectTypeID', 'caseTypeID');
  }
}

export default new ObjectTransformer();
